import { Injectable, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import {
  Router,
  NavigationEnd,
  ActivatedRoute,
  ActivatedRouteSnapshot,
} from '@angular/router'
import { filter, startWith, map } from 'rxjs'

/**
 * HydrationService provides reactive access (signals) to the
 * current route’s path‐ and query‐parameters, always synced on
 * each NavigationEnd event.
 */
@Injectable({ providedIn: 'root' })
export class HydrationService {
  private readonly rootRoute = inject(ActivatedRoute)
  private readonly router = inject(Router)

  /**
   * Fires on every NavigationEnd so downstream computeds re-run.
   */
  private readonly navigationEndSignal = toSignal<NavigationEnd | null>(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
    { initialValue: null },
  )

  /**
   * The *deepest* activated route snapshot.
   * Walks from root → firstChild until the leaf.
   */
  public readonly leafSnapshot = computed<ActivatedRouteSnapshot>(() => {
    this.navigationEndSignal()
    let route = this.router.routerState.snapshot.root
    while (route.firstChild) {
      route = route.firstChild
    }
    return route
  })

  /**
   * Returns a signal of the current path-param by name.
   * Emits null if absent.
   */
  param(name: string) {
    return computed<string | null>(() => {
      let route: ActivatedRouteSnapshot | null = this.leafSnapshot()
      while (route) {
        const v = route.paramMap.get(name)
        if (v !== null) return v
        route = route.parent
      }
      return null
    })
  }

  /**
   * Returns a signal of the current query-param by name.
   * Seeds with `initial` if absent.
   */
  query(name: string, initial: string | null = null) {
    return computed<string | null>(() => {
      const v = this.leafSnapshot().queryParamMap.get(name)
      return v === null ? initial : v
    })
  }

  /**
   * Returns a signal of a comma-separated query-param as a string[].
   * E.g. `?tags=a,b,c` → `['a','b','c']`.
   */
  arrayQuery(name: string) {
    return computed<string[]>(() => {
      const raw = this.leafSnapshot().queryParamMap.get(name)
      return raw ? raw.split(',').filter(Boolean) : []
    })
  }

  /**
   * Returns a boolean signal for a query-param, e.g. `?flag=true`.
   */
  boolQuery(name: string) {
    const sig = this.query(name)
    return computed<boolean>(() => sig() === 'true')
  }

  /** Shorthand for the common “id” path-param */
  readonly huntId = this.param('id')

  /** Shorthand for the common “packageId” path-param */
  readonly packageId = this.param('packageId')
}
